import {
  ContractPrerequisite,
  defineConfig,
  LegalBases,
  Retention,
  Voluntary,
} from '@policystack/sdk';
import { brand } from '@repo/brand';
import { manualResolver } from '@policystack/core/consent';
import { cookieAdapter } from '@policystack/core/consent/storage/cookie';

import * as scanned from './policystack.gen';

/** The consent store's persistence, exported so tests can mint the same cookie. */
export const consentCookie = cookieAdapter({ name: `${brand.slug}_consent` });

// The privacy policy, cookie policy, and consent runtime all derive from
// this one config (see @policystack/sdk). Scanned data categories, cookies,
// and third parties come from ./policystack.gen, generated by the
// policyStack() Vite plugin.
//
// Forks: replace the company block with your real legal entity (the address
// is a placeholder, like MAIL_FROM and STRIPE_PRICE_PAID), review every
// purpose and retention below, and have a lawyer read the rendered output
// before launch. PolicyStack is not legal advice.
export default defineConfig(
  {
    company: {
      name: brand.name,
      legalName: 'JXD Ltd',
      address: 'Replace with your registered company address',
      url: brand.url,
      contact: { email: brand.email },
      // Declares no DPO is appointed; appoint one and set details if your
      // processing requires it.
      dpo: { required: false },
    },
    effectiveDate: '2026-07-27',
    // UK company serving UK, EEA, and Californian users. All three are
    // hand-authored ('specific') policy texts in PolicyStack.
    jurisdictions: ['uk', 'eea', 'us-ca'],
    data: {
      collected: {
        'Account Information': ['Name', 'Email address', 'Profile image', 'Language preference'],
        'Organization Data': ['Organization name', 'Membership and roles', 'Invitations'],
        'Workspace Content': ['Notes', 'Tasks', 'Uploaded images', 'Assistant conversations'],
        'Usage Data': ['Pages visited', 'Product events', 'Browser type'],
      },
      context: {
        'Account Information': {
          purpose: 'To authenticate you and operate your account',
          lawfulBasis: LegalBases.Contract,
          retention: Retention.UntilAccountDeletion,
          provision: ContractPrerequisite('We cannot create or operate your account.'),
        },
        'Organization Data': {
          purpose: 'To run shared workspaces, memberships, and billing per organization',
          lawfulBasis: LegalBases.Contract,
          retention: Retention.UntilAccountDeletion,
          provision: ContractPrerequisite('We cannot provide shared workspaces.'),
        },
        'Workspace Content': {
          purpose: 'To store and sync the content you and your organization create',
          lawfulBasis: LegalBases.Contract,
          retention: Retention.UntilAccountDeletion,
          provision: ContractPrerequisite('We cannot store or sync your content.'),
        },
        'Usage Data': {
          purpose: 'To understand product usage and improve the service',
          lawfulBasis: LegalBases.LegitimateInterests,
          retention: Retention.NinetyDays,
          provision: Voluntary('Product analytics only; the service works without it.'),
        },
      },
    },
    // Each enabled key becomes a consent category; 'consent' as the basis is
    // what gates it behind the banner. The session, cookie-cache, and locale
    // cookies are essential: the locale cookie stores an explicit user
    // choice, not tracking, so it is deliberately classified here.
    cookies: {
      used: { essential: true, analytics: true },
      context: {
        essential: { lawfulBasis: LegalBases.Contract },
        analytics: { lawfulBasis: LegalBases.Consent },
      },
    },
    thirdParties: [
      {
        name: 'Cloudflare',
        purpose: 'Hosting, storage, and network security',
        policyUrl: 'https://www.cloudflare.com/privacypolicy/',
      },
      {
        name: 'Stripe',
        purpose: 'Subscription billing and payment processing',
        policyUrl: 'https://stripe.com/privacy',
      },
      {
        name: 'Resend',
        purpose: 'Transactional and notification email delivery',
        policyUrl: 'https://resend.com/legal/privacy-policy',
      },
      {
        name: 'OpenPanel',
        purpose: 'Product analytics',
        policyUrl: 'https://openpanel.dev/privacy',
      },
      {
        name: 'Anthropic',
        purpose: 'AI assistant responses',
        policyUrl: 'https://www.anthropic.com/legal/privacy',
      },
    ],
    trackingTechnologies: ['local storage'],
    automatedDecisionMaking: [],
    consent: {
      // Cookie persistence so the choice survives across subdomains and is
      // upgradeable to a server-side read later.
      adapter: consentCookie,
      // Constant opt-in posture: legal everywhere we operate, deterministic
      // between server and client render, and a banner for every visitor.
      // Swap for headerResolver() or clientGeoResolver() to show US visitors
      // an opt-out posture instead.
      jurisdictionResolver: manualResolver('uk'),
    },
  },
  scanned,
);
